var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { RoomProductType, CancellationPolicyScenarioEnum, HotelCancellationPolicyV2Enum, LodgingCollectionEnum, StayType, TabViewEnum, HotelStarRatingEnum, } from "./interfaces";
import { CfarQuoteEnum, CfarQuoteResultEnum, } from "../apis/tysons";
/**
 * Reads the BFF `promotionKind` signal (`MemberOnlyDeal` / `NthNight`) off the pricing object.
 * The field isn't on this repo's hand-authored types (only the embedded @capone/portal bundle
 * references it), so it's read structurally via `in` — mirroring b2bportal's getPromotionKind.
 */
export var getPromotionKind = function () {
    var sources = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        sources[_i] = arguments[_i];
    }
    for (var _a = 0, sources_1 = sources; _a < sources_1.length; _a++) {
        var source = sources_1[_a];
        if (source != null && "promotionKind" in source) {
            var kind = source.promotionKind;
            if (kind === "MemberOnlyDeal" || kind === "NthNight") {
                return kind;
            }
        }
    }
    return undefined;
};
/**
 * Promotion analytics props mirroring b2bportal's getAvailabilityPromotionTracking, keyed off
 * the `promotionKind` signal. `promotion_amount` is the strikethrough savings:
 * - MemberOnlyDeal: `priceWithNoDiscounts − priceWithUnmanagedDiscounts` on the total
 *   discount-aware prices (the taxes/fees cancel vs. the reconstructed strikethrough).
 * - NthNight: `nthNightPromotion.originalPriceTotal − totalPrice`.
 * Omitted when not derivable / not positive; the two optional keys are omitted when no
 * promotion applies.
 */
export var getPromotionTrackingProperties = function (lodging) {
    var _a, _b, _c, _d, _e;
    var price = lodging === null || lodging === void 0 ? void 0 : lodging.price;
    var promotion_type = getPromotionKind(price);
    if (price == null || promotion_type == null) {
        return {};
    }
    var promotion_amount;
    if (promotion_type === "NthNight") {
        var original = (_b = (_a = lodging === null || lodging === void 0 ? void 0 : lodging.nthNightPromotion) === null || _a === void 0 ? void 0 : _a.originalPriceTotal) === null || _b === void 0 ? void 0 : _b.fiat.value;
        var promoted = (_c = price.totalPrice) === null || _c === void 0 ? void 0 : _c.fiat.value;
        if (original != null && promoted != null && original - promoted > 0) {
            promotion_amount = original - promoted;
        }
    }
    else {
        var noDiscount = (_d = price.totalDiscountAware) === null || _d === void 0 ? void 0 : _d.priceWithNoDiscounts.fiat.value;
        var withDiscount = (_e = price.totalDiscountAware) === null || _e === void 0 ? void 0 : _e.priceWithUnmanagedDiscounts.fiat.value;
        if (noDiscount != null &&
            withDiscount != null &&
            noDiscount - withDiscount > 0) {
            promotion_amount = noDiscount - withDiscount;
        }
    }
    return { promotion_type: promotion_type, promotion_amount: promotion_amount };
};
export var getCancellationPolicyScenarioEnumByRoomIndex = function (_a) {
    var _b;
    var roomIndex = _a.roomIndex, roomInfoProducts = _a.roomInfoProducts, getHotelAdditionalInfoByIndex = _a.getHotelAdditionalInfoByIndex, _c = _a.isRefundableRoomsWithPartiallyRefundablePolicyEnabled, isRefundableRoomsWithPartiallyRefundablePolicyEnabled = _c === void 0 ? false : _c;
    if (!isRefundableRoomsWithPartiallyRefundablePolicyEnabled) {
        return CancellationPolicyScenarioEnum.Other;
    }
    var hasOfferWithCfarPremiumRefunded = false;
    var hasOfferWithoutCfarPremiumRefunded = false;
    var products = (_b = roomInfoProducts[roomIndex]) === null || _b === void 0 ? void 0 : _b.products;
    products === null || products === void 0 ? void 0 : products.forEach(function (_, productIndex) {
        var additionalInfo = getHotelAdditionalInfoByIndex({
            roomInfoIndex: roomIndex,
            productIndex: productIndex,
        });
        if (!!(additionalInfo === null || additionalInfo === void 0 ? void 0 : additionalInfo.shouldRefundCfarPremium)) {
            hasOfferWithCfarPremiumRefunded = true;
        }
        else {
            hasOfferWithoutCfarPremiumRefunded = true;
        }
    });
    if (hasOfferWithCfarPremiumRefunded && hasOfferWithoutCfarPremiumRefunded) {
        return CancellationPolicyScenarioEnum.TwoHotelOptionsOneNonRefOnePartiallyRef;
    }
    else {
        return CancellationPolicyScenarioEnum.Other;
    }
};
export var getShopRoomInfoProductsWithTransformedIndexes = function (_a) {
    var roomInfoProducts = _a.roomInfoProducts, getHotelCfarQuoteByIndex = _a.getHotelCfarQuoteByIndex, cfarQuotes = _a.cfarQuotes, getHotelCancellationPolicyByIndex = _a.getHotelCancellationPolicyByIndex, getHotelAdditionalInfoByIndex = _a.getHotelAdditionalInfoByIndex, nightCount = _a.nightCount, pricesHelpers = _a.pricesHelpers, _b = _a.isRefundableRoomsWithPartiallyRefundablePolicyEnabled, isRefundableRoomsWithPartiallyRefundablePolicyEnabled = _b === void 0 ? false : _b, _c = _a.isHotelCfarRrMultipleCancellationPoliciesEnabled, isHotelCfarRrMultipleCancellationPoliciesEnabled = _c === void 0 ? false : _c, isHotelCfarModelV1Enabled = _a.isHotelCfarModelV1Enabled, shouldDisableRefundableRooms = _a.shouldDisableRefundableRooms, matchCfarQuotesWithRoomId = _a.matchCfarQuotesWithRoomId, displayTaxesAndFeesIncluded = _a.displayTaxesAndFeesIncluded, displayTaxesAndFeesIncludedNightlyPrice = _a.displayTaxesAndFeesIncludedNightlyPrice;
    var getModifiedLegacyPrices = pricesHelpers.getModifiedLegacyPrices, getAddedLegacyPrices = pricesHelpers.getAddedLegacyPrices;
    var result = [];
    roomInfoProducts.forEach(function (roomInfoProduct, roomIndex) {
        var products = [];
        var cancellationPolicyScenarioEnum = getCancellationPolicyScenarioEnumByRoomIndex({
            roomIndex: roomIndex,
            roomInfoProducts: roomInfoProducts,
            getHotelAdditionalInfoByIndex: getHotelAdditionalInfoByIndex,
            isRefundableRoomsWithPartiallyRefundablePolicyEnabled: isRefundableRoomsWithPartiallyRefundablePolicyEnabled,
        });
        var hotelRoomId = roomInfoProduct.roomInfo.roomId;
        // the cfar quotes for this room
        var roomAssociatedCfarQuotes = cfarQuotes === null || cfarQuotes === void 0 ? void 0 : cfarQuotes.find(function (cfarQuote) { return cfarQuote.roomInfo.roomId === hotelRoomId; });
        var getCancellationPolicyScenarioEnumByRoomId = function () {
            var hasOfferWithCfarPremiumRefunded = false;
            var hasOfferWithoutCfarPremiumRefunded = false;
            roomAssociatedCfarQuotes === null || roomAssociatedCfarQuotes === void 0 ? void 0 : roomAssociatedCfarQuotes.cfarQuotes.forEach(function (quote) {
                if (quote.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
                    var additionalInfo = quote.additionalInfo;
                    if (additionalInfo.shouldRefundCfarPremium) {
                        hasOfferWithCfarPremiumRefunded = true;
                    }
                    else {
                        hasOfferWithoutCfarPremiumRefunded = true;
                    }
                }
            });
            // replicating the logic of getCancellationPolicyScenarioEnumByRoomIndex except
            // identifying the cfar quotes based off room id
            var cancellationPolicyScenarioEnumByRoomId = !isRefundableRoomsWithPartiallyRefundablePolicyEnabled
                ? CancellationPolicyScenarioEnum.Other
                : hasOfferWithCfarPremiumRefunded &&
                    hasOfferWithoutCfarPremiumRefunded
                    ? CancellationPolicyScenarioEnum.TwoHotelOptionsOneNonRefOnePartiallyRef
                    : CancellationPolicyScenarioEnum.Other;
            return cancellationPolicyScenarioEnumByRoomId;
        };
        var hasAddedRefundableRoom = false;
        var hasDisplayedNonrefundable = false;
        roomInfoProduct.products.forEach(function (roomProduct, productIndex) {
            var baseProduct = {
                roomIndex: roomIndex,
                productIndex: productIndex,
                rateId: roomProduct.rateId,
            };
            products.push(__assign(__assign({}, roomProduct), { roomProductVariant: {
                    baseProduct: baseProduct,
                    variant: RoomProductType.Default,
                } }));
            // refundable room
            var associatedProductCfarQuote = roomAssociatedCfarQuotes === null || roomAssociatedCfarQuotes === void 0 ? void 0 : roomAssociatedCfarQuotes.cfarQuotes.find(function (quote) {
                var _a, _b;
                if (quote.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
                    return ((_a = quote.rateId) === null || _a === void 0 ? void 0 : _a.value) === ((_b = roomProduct.rateId) === null || _b === void 0 ? void 0 : _b.value);
                }
                return false;
            });
            var cfarQuote = getHotelCfarQuoteByIndex({
                roomInfoIndex: roomIndex,
                productIndex: productIndex,
            });
            var getCfarQuoteByRoomId = function () {
                if ((associatedProductCfarQuote === null || associatedProductCfarQuote === void 0 ? void 0 : associatedProductCfarQuote.CfarQuote) ===
                    CfarQuoteEnum.HotelCfarEligible) {
                    if (associatedProductCfarQuote.cfarQuoteResult.CfarQuoteResult ===
                        CfarQuoteResultEnum.HotelCfarQuote) {
                        return associatedProductCfarQuote.cfarQuoteResult;
                    }
                }
                return null;
            };
            var additionalInfo = getHotelAdditionalInfoByIndex({
                roomInfoIndex: roomIndex,
                productIndex: productIndex,
            });
            var getAdditionalInfoByRoomId = function () {
                return (associatedProductCfarQuote === null || associatedProductCfarQuote === void 0 ? void 0 : associatedProductCfarQuote.CfarQuote) ===
                    CfarQuoteEnum.HotelCfarEligible
                    ? associatedProductCfarQuote.additionalInfo
                    : null;
            };
            var cancellationPolicy = getHotelCancellationPolicyByIndex({
                roomInfoIndex: roomIndex,
                productIndex: productIndex,
            });
            var getCfarCancellationPolicyByRoomId = function () {
                return (associatedProductCfarQuote === null || associatedProductCfarQuote === void 0 ? void 0 : associatedProductCfarQuote.CfarQuote) ===
                    CfarQuoteEnum.HotelCfarEligible
                    ? associatedProductCfarQuote.cancellationPolicyV2
                    : null;
            };
            var _a = {
                validCfarQuote: matchCfarQuotesWithRoomId
                    ? getCfarQuoteByRoomId()
                    : cfarQuote,
                validCancellationPolicy: matchCfarQuotesWithRoomId
                    ? getCfarCancellationPolicyByRoomId()
                    : cancellationPolicy,
                validAdditionalInfo: matchCfarQuotesWithRoomId
                    ? getAdditionalInfoByRoomId()
                    : additionalInfo,
                validCancellationPolicyEnum: matchCfarQuotesWithRoomId
                    ? getCancellationPolicyScenarioEnumByRoomId()
                    : cancellationPolicyScenarioEnum,
            }, validCfarQuote = _a.validCfarQuote, validCancellationPolicy = _a.validCancellationPolicy, validAdditionalInfo = _a.validAdditionalInfo, validCancellationPolicyEnum = _a.validCancellationPolicyEnum;
            var cancellationPolicyScenario = {
                type: validCancellationPolicyEnum,
            };
            var isNonRefundable = (validCancellationPolicy === null || validCancellationPolicy === void 0 ? void 0 : validCancellationPolicy.CancellationPolicyV2) ===
                HotelCancellationPolicyV2Enum.NonRefundable;
            /*
              note: if there is a non-refundable and partially refundable option by the hotel the user will see a
              “Fully refundable by Capital One Travel until {X date}” that's built off the hotel’s partially refundable rate
      
              for more details, see https://hopper-jira.atlassian.net/browse/BF-2241
      
              also used for https://hopper-jira.atlassian.net/browse/BF-2274
            */
            var isTwoHotelOptionsConditionSatisfied = cancellationPolicyScenario.type ===
                CancellationPolicyScenarioEnum.Other ||
                (cancellationPolicyScenario.type ===
                    CancellationPolicyScenarioEnum.TwoHotelOptionsOneNonRefOnePartiallyRef &&
                    /*
                      note: rule 5 - if there is a non-refundable and partially refundable option by the hotel, the user should not
                      get a refundable room rate https://hopper-jira.atlassian.net/browse/BF-2274
                    */
                    !isHotelCfarRrMultipleCancellationPoliciesEnabled &&
                    !!(validAdditionalInfo === null || validAdditionalInfo === void 0 ? void 0 : validAdditionalInfo.shouldRefundCfarPremium));
            // If the flag isRefundableByCapitalOne is returned on the cfarQuote, we will
            // use that quote to create the "Refundable By Capital One Travel" rate, bypassing
            // the business logic implemented here on the FE
            // see https://hopper-jira.atlassian.net/browse/FLEX-3576 for more context
            var addCfarQuote = function (_a) {
                var _b, _c, _d;
                var overrideFilteringLogic = _a.overrideFilteringLogic, isHotelCfarModelV1Enabled = _a.isHotelCfarModelV1Enabled;
                if (!validCfarQuote) {
                    return;
                }
                var quoteId = validCfarQuote.id, premiumPrices = validCfarQuote.premiumPrices, coverageAmount = validCfarQuote.coverageAmount, coveragePercentage = validCfarQuote.coveragePercentage, pricingDescription = validCfarQuote.pricingDescription;
                var totalDiscountAware = (roomProduct === null || roomProduct === void 0 ? void 0 : roomProduct.totalDiscountAware)
                    ? __assign(__assign({}, roomProduct.totalDiscountAware), { priceWithUnmanagedDiscounts: getAddedLegacyPrices(roomProduct.totalDiscountAware.priceWithUnmanagedDiscounts, premiumPrices) }) : undefined;
                var sellRate = getAddedLegacyPrices(roomProduct.sellRate, premiumPrices);
                var premiumPricesPerNight = getModifiedLegacyPrices(premiumPrices, function (total) { return Math.ceil(total / (nightCount !== null && nightCount !== void 0 ? nightCount : 1)); } // Rounding here for consistency so we don't rely on the implementation of other modules
                );
                var addQuote = function () {
                    products.push(__assign(__assign(__assign(__assign(__assign(__assign({}, roomProduct), (displayTaxesAndFeesIncluded
                        ? {
                            tripTotal: getAddedLegacyPrices(roomProduct.tripTotal, premiumPrices),
                            total: getAddedLegacyPrices(roomProduct.total, premiumPrices),
                        }
                        : undefined)), (totalDiscountAware ? { totalDiscountAware: totalDiscountAware } : undefined)), { sellRate: sellRate }), (displayTaxesAndFeesIncludedNightlyPrice &&
                        (roomProduct === null || roomProduct === void 0 ? void 0 : roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded)
                        ? {
                            nightlyDiscountAware: __assign(__assign({}, roomProduct === null || roomProduct === void 0 ? void 0 : roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded), { priceWithUnmanagedDiscounts: getAddedLegacyPrices(roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded
                                    .priceWithUnmanagedDiscounts, premiumPricesPerNight) }),
                        }
                        : (roomProduct === null || roomProduct === void 0 ? void 0 : roomProduct.nightlyDiscountAware)
                            ? {
                                nightlyDiscountAware: __assign(__assign({}, roomProduct.nightlyDiscountAware), { priceWithUnmanagedDiscounts: getAddedLegacyPrices(roomProduct.nightlyDiscountAware
                                        .priceWithUnmanagedDiscounts, premiumPricesPerNight) }),
                            }
                            : undefined)), { perNightSellRate: displayTaxesAndFeesIncludedNightlyPrice &&
                            (roomProduct === null || roomProduct === void 0 ? void 0 : roomProduct.perNightSellRateTaxesAndFeesIncluded)
                            ? getAddedLegacyPrices(roomProduct.perNightSellRateTaxesAndFeesIncluded, premiumPricesPerNight)
                            : getAddedLegacyPrices(roomProduct.perNightSellRate, premiumPricesPerNight), perNightSellRateTaxesAndFeesIncluded: roomProduct.perNightSellRateTaxesAndFeesIncluded
                            ? getAddedLegacyPrices(roomProduct.perNightSellRateTaxesAndFeesIncluded, premiumPricesPerNight)
                            : roomProduct.perNightSellRateTaxesAndFeesIncluded, nightlyDiscountAwareTaxesAndFeesIncluded: roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded
                            ? Object.keys(roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded).reduce(function (acc, curr) {
                                acc[curr] = getAddedLegacyPrices(roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded[curr], premiumPricesPerNight);
                                return acc;
                            }, {})
                            : roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded, tripTotalExcludingTaxes: roomProduct.tripTotalExcludingTaxes
                            ? getAddedLegacyPrices(roomProduct.tripTotalExcludingTaxes, 
                            // Issue with the types from redmond and b2b-interfaces
                            premiumPrices)
                            : roomProduct.tripTotalExcludingTaxes, roomProductVariant: {
                            premiumPrices: premiumPrices,
                            premiumPricesPerNight: premiumPricesPerNight,
                            quoteId: quoteId,
                            coveragePercentage: coveragePercentage,
                            coverageAmount: coverageAmount,
                            baseProduct: baseProduct,
                            pricingDescription: pricingDescription,
                            additionalInfo: validAdditionalInfo,
                            cancellationPolicy: validCancellationPolicy,
                            cancellationPolicyScenario: cancellationPolicyScenario,
                            variant: RoomProductType.RefundableRoom,
                        } }));
                    hasAddedRefundableRoom = true;
                };
                if (isHotelCfarModelV1Enabled) {
                    if (overrideFilteringLogic) {
                        addQuote();
                    }
                    return;
                }
                if (
                /*
                note: rule 1 - it adds the filtering rule to show the rate with the highest value only;
                see https://hopper-jira.atlassian.net/browse/BF-1407
              */
                !hasAddedRefundableRoom &&
                    // note: rule 3 - address multiple nonrefundable room products; see https://hopper-jira.atlassian.net/browse/BF-1716
                    !(hasDisplayedNonrefundable && isNonRefundable) &&
                    /*
                    note: rule 4 - if there is a non-refundable and partially refundable option by the hotel the user will see
                    a “Fully refundable by Capital One Travel until {X date}” that's built off the hotel’s partially refundable rate
                  */
                    isTwoHotelOptionsConditionSatisfied) {
                    var nextProductIndex = productIndex + 1;
                    var nextProduct = roomInfoProduct.products[nextProductIndex];
                    var isMoreExpensiveThanNextProduct = nextProductIndex < roomInfoProduct.products.length
                        ? ((_b = totalDiscountAware === null || totalDiscountAware === void 0 ? void 0 : totalDiscountAware.priceWithUnmanagedDiscounts.fiat.value) !== null && _b !== void 0 ? _b : sellRate.fiat.value) >
                            ((_d = (_c = nextProduct.totalDiscountAware) === null || _c === void 0 ? void 0 : _c.priceWithUnmanagedDiscounts.fiat.value) !== null && _d !== void 0 ? _d : nextProduct.sellRate.fiat.value)
                        : false;
                    /*
                    note: rule 2 - don't show made refundable room by capital one when more expensive than refundable rate;
                    see https://hopper-jira.atlassian.net/browse/BF-1691
                    */
                    if (!isMoreExpensiveThanNextProduct) {
                        addQuote();
                    }
                }
            };
            // we want to add the ability to hide RR, but not standalone CFAR for certain hotels
            // this flag comes from Tysons and is set at the hotel level
            if (!shouldDisableRefundableRooms) {
                addCfarQuote({
                    overrideFilteringLogic: validAdditionalInfo === null || validAdditionalInfo === void 0 ? void 0 : validAdditionalInfo.isRefundableByCapitalOne,
                    isHotelCfarModelV1Enabled: isHotelCfarModelV1Enabled,
                });
                if (isNonRefundable) {
                    hasDisplayedNonrefundable = true;
                }
            }
        });
        result.push(__assign(__assign({}, roomInfoProduct), { products: products }));
    });
    return result;
};
export var getNthNightTrackingProperty = function (lodgingData) {
    return (lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.nthNightPromotion)
        ? lodgingData.nthNightPromotion.isLocked
            ? "locked"
            : "unlocked"
        : "none";
};
export var getProductTypeTrackingProperty = function (lodgingData) {
    var collection = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.lodgingCollection;
    return collection === LodgingCollectionEnum.Premier ||
        collection === LodgingCollectionEnum.Lifestyle
        ? collection
        : "";
};
export var getStaysTabTrackingProperty = function (staysTypes) {
    var stayType = staysTypes[0];
    switch (stayType) {
        case StayType.Hotels:
            return TabViewEnum.HOTEL;
        case StayType.Homes:
            return TabViewEnum.VACATION_RENTAL;
        default:
            return staysTypes.length === 0 ? TabViewEnum.ALL_STAYS : "";
    }
};
export var getLodgingTrackingProperties = function (lodgingData) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    if (!lodgingData) {
        return { properties: {}, encryptedProperties: [] };
    }
    var comparableRate = null;
    if (((_a = lodgingData.price) === null || _a === void 0 ? void 0 : _a.comparePrices) &&
        ((_b = lodgingData.price) === null || _b === void 0 ? void 0 : _b.comparePrices.length) > 0) {
        comparableRate = lodgingData.price.comparePrices[0];
    }
    return {
        properties: __assign(__assign(__assign({ metadata_provider: ((_c = lodgingData.price) === null || _c === void 0 ? void 0 : _c.providerName) || "N/A", metadata_count: lodgingData.lodging.media.length || 0, is_pc: lodgingData.lodgingCollection === LodgingCollectionEnum.Premier, promotion_applicable: (_d = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestPromotionThisLodging) === null || _d === void 0 ? void 0 : _d.promotionType, promotion_discount: (_e = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestPromotionThisLodging) === null || _e === void 0 ? void 0 : _e.discountAmount.amount, promotion_discount_currency: (_f = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestPromotionThisLodging) === null || _f === void 0 ? void 0 : _f.discountAmount.currency, promotion_discount_percentage: (_g = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestPromotionThisLodging) === null || _g === void 0 ? void 0 : _g.discountPercentage, lowest_comparable_daily_including_taxes_fees_usd: comparableRate === null || comparableRate === void 0 ? void 0 : comparableRate.nightlyPrice.fiat.value, lowest_comparable_daily_including_taxes_fees_user_currency: comparableRate === null || comparableRate === void 0 ? void 0 : comparableRate.nightlyPrice.fiat.currencyCode, lowest_comparable_provider: comparableRate === null || comparableRate === void 0 ? void 0 : comparableRate.source, provider_name: (_h = lodgingData.price) === null || _h === void 0 ? void 0 : _h.providerName, rate_provider: (_j = lodgingData.price) === null || _j === void 0 ? void 0 : _j.providerName }, (_k = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _k === void 0 ? void 0 : _k.properties), (_m = (_l = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestOfferThisLodging) === null || _l === void 0 ? void 0 : _l.trackingPropertiesV2) === null || _m === void 0 ? void 0 : _m.properties), { has_offer: !!(lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestOfferThisLodging), is_lc: lodgingData.lodgingCollection === LodgingCollectionEnum.Lifestyle, loyalty_program_code: lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.loyaltyProgramCode, nth_night_promo: getNthNightTrackingProperty(lodgingData), meal_plan: (_p = (_o = lodgingData.price) === null || _o === void 0 ? void 0 : _o.mealPlan) === null || _p === void 0 ? void 0 : _p.kind, lodging_tripadvisor_rating: (_q = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.lodging.tripAdvisorReviews) === null || _q === void 0 ? void 0 : _q.overallScore.toString(), product_type: getProductTypeTrackingProperty(lodgingData), is_corporate: lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.hasCorporateRates }),
        encryptedProperties: [
            (_s = (_r = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _r === void 0 ? void 0 : _r.encryptedProperties) !== null && _s !== void 0 ? _s : "",
            (_v = (_u = (_t = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.bestOfferThisLodging) === null || _t === void 0 ? void 0 : _t.trackingPropertiesV2) === null || _u === void 0 ? void 0 : _u.encryptedProperties) !== null && _v !== void 0 ? _v : "",
        ],
    };
};
// Helper function to build encoded persistent lodging tracking properties from the lodging object.
export var buildEncodedPersistentLodgingTrackingProperties = function (lodgingData) {
    if (!lodgingData) {
        return null;
    }
    return encodePersistentLodgingTrackingProperties(getPersistentLodgingTrackingProperties(lodgingData));
};
export var getPersistentLodgingTrackingProperties = function (lodgingData) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (!lodgingData) {
        return null;
    }
    return __assign(__assign(__assign({}, (((_b = (_a = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.is_boosted) && {
        is_boosted: (_d = (_c = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _c === void 0 ? void 0 : _c.properties) === null || _d === void 0 ? void 0 : _d.is_boosted,
    })), (((_f = (_e = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _e === void 0 ? void 0 : _e.properties) === null || _f === void 0 ? void 0 : _f.availability_id) && {
        availability_id: (_h = (_g = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _g === void 0 ? void 0 : _g.properties) === null || _h === void 0 ? void 0 : _h.availability_id,
    })), (((_k = (_j = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _j === void 0 ? void 0 : _j.properties) === null || _k === void 0 ? void 0 : _k.pricing_engine_pricing_id) && {
        pricing_engine_pricing_id: (_m = (_l = lodgingData === null || lodgingData === void 0 ? void 0 : lodgingData.trackingPropertiesV2) === null || _l === void 0 ? void 0 : _l.properties) === null || _m === void 0 ? void 0 : _m.pricing_engine_pricing_id,
    }));
};
export var encodePersistentLodgingTrackingProperties = function (trackingProperties) {
    if (!trackingProperties) {
        return null;
    }
    return encodeURIComponent(JSON.stringify(trackingProperties));
};
export var decodePersistentLodgingTrackingProperties = function (encodedProperties) {
    try {
        var decodedProperties = decodeURIComponent(encodedProperties);
        var parsedProperties = JSON.parse(decodedProperties);
        return __assign(__assign(__assign({}, (parsedProperties.availability_id && {
            availability_id: parsedProperties.availability_id,
        })), (parsedProperties.pricing_engine_pricing_id && {
            pricing_engine_pricing_id: parsedProperties.pricing_engine_pricing_id,
        })), (parsedProperties.is_boosted && {
            is_boosted: parsedProperties.is_boosted,
        }));
    }
    catch (_a) {
        return null;
    }
};
export var MILES = "Miles";
export var CASH = "Cash";
export var POINTS = "Points";
export var ValidRewardsCurrency = [MILES, CASH, POINTS];
export var RewardsMultipliers = {
    Miles: {
        currency: MILES,
        multiplier: 100,
    },
    Cash: {
        currency: CASH,
        multiplier: 1,
    },
    Points: {
        currency: POINTS,
        multiplier: 100,
    }
};
export function hotelStarRatingToNumber(starRating) {
    switch (starRating) {
        case HotelStarRatingEnum.NotAvailable:
            return 0;
        case HotelStarRatingEnum.One:
            return 1;
        case HotelStarRatingEnum.Two:
            return 2;
        case HotelStarRatingEnum.Three:
            return 3;
        case HotelStarRatingEnum.Four:
            return 4;
        case HotelStarRatingEnum.Five:
            return 5;
        default:
            return 0;
    }
}
//# sourceMappingURL=utils.js.map